import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import axios from "axios"
import { API } from "../../config"
import { trackException, trackStackTrace } from '../../utils/logger'
import i18n from "../../i18n.js"

type NewHireAPIError = {
    code: string
    message: string
}

interface INewHire {
    userID: string
    verifyDetails: {
        verifyID: string
        isVerified: boolean
        isInternal: boolean
    },
    password: string
    isLoading: boolean
    errorMessage: null | string
    errorCode: null | string
    onboardingTeamEmail: string
}

// NewHireVerifyAPIResponse defines verify link API call response body
type NewHireVerifyAPIResponse = {
    userID: string
    onboardingTeamEmail: string
    isInternal: boolean
}

// VerifyNewHireSuccessPayload defines payload parameters
type VerifyNewHireSuccessPayload = {
    verifyID: string
    isVerified: boolean
    userID: string
    onboardingTeamEmail: string
    isInternal: boolean
}

const initialState: INewHire = {
    userID: '',
    verifyDetails: {
        verifyID: '',
        isVerified: false,
        isInternal: true,
    },
    password: '',
    isLoading: false,
    errorMessage: null,
    errorCode: null,
    onboardingTeamEmail: ''
}

const FALLBACK_ERROR_CODE = "INTERNAL_SERVER_ERROR"

const getCurrentLocale = (): "en" | "fr" => {
    const current = i18n.language ?? "en"
    return current.toLowerCase().startsWith("fr") ? "fr" : "en"
}

const getRequestHeaders = () => {
    const locale = getCurrentLocale()
    return {
        "Content-Type": "application/json",
        "Accept-Language": locale,
        "X-Locale": locale,
    }
}

const localizeErrorFromCode = (code: string, apiMessage: string): string => {
    if (apiMessage.trim()) {
        return apiMessage
    }
    return i18n.t(`errors:codes.${code}`, {
        defaultValue: i18n.t("errors:codes.INTERNAL_SERVER_ERROR"),
    }) as string
}

/** Returns the first non-blank string from candidates, or the i18n fallback. */
function resolveMessage(maybeMessage: unknown, fallback: unknown): string {
    if (typeof maybeMessage === "string" && maybeMessage.trim()) return maybeMessage
    if (typeof fallback === "string" && (fallback as string).trim()) return fallback as string
    return i18n.t("errors:codes.INTERNAL_SERVER_ERROR")
}

/** Returns the first non-blank string code, or FALLBACK_ERROR_CODE. */
function resolveCode(maybeCode: unknown): string {
    if (typeof maybeCode === "string" && maybeCode.trim()) return maybeCode
    return FALLBACK_ERROR_CODE
}

const parseAPIError = (err: any): NewHireAPIError => {
    const rawData = err?.response?.data

    if (rawData && typeof rawData === "object") {
        const raw = rawData as { code?: unknown; message?: unknown }
        return {
            code: resolveCode(raw.code),
            message: resolveMessage(raw.message, err?.message),
        }
    }

    if (typeof rawData === "string" && rawData.trim()) {
        return { code: FALLBACK_ERROR_CODE, message: rawData }
    }

    return { code: FALLBACK_ERROR_CODE, message: resolveMessage(err?.message, null) }
}

const newHireSlice = createSlice({
    name: 'new-hire',
    initialState,
    reducers: {
        verifyNewHireLoading(state) {
            state.isLoading = true
        },
        verifyNewHireSuccess(state, action: PayloadAction<VerifyNewHireSuccessPayload>) {
            state.isLoading = false
            state.errorCode = null
            state.errorMessage = null
            state.verifyDetails.verifyID = action.payload.verifyID
            state.verifyDetails.isVerified = action.payload.isVerified
            state.userID = action.payload.userID
            state.onboardingTeamEmail = action.payload.onboardingTeamEmail
            state.verifyDetails.isInternal = action.payload.isInternal
        },
        verifyNewHiresError(state, action: PayloadAction<NewHireAPIError>) {
            state.isLoading = false
            state.errorCode = action.payload.code
            state.errorMessage = action.payload.message
        },
        validateAssetLabelLoading(state) {
            state.isLoading = true
        },
        validateAssetLabelSuccess(state, action: PayloadAction<{ password: string }>) {
            state.isLoading = false
            state.errorCode = null
            state.errorMessage = null
            state.password = action.payload.password
        },
        validateAssetLabelError(state, action: PayloadAction<NewHireAPIError>) {
            state.isLoading = false
            state.errorCode = action.payload.code
            state.errorMessage = action.payload.message
        },
        clearErrorMessage(state) {
            state.errorMessage = null
            state.errorCode = null
        },
    },
})

const { reducer, actions } = newHireSlice
// export actions
export const {
    verifyNewHireLoading,
    verifyNewHireSuccess,
    verifyNewHiresError,
    validateAssetLabelLoading,
    validateAssetLabelSuccess,
    validateAssetLabelError,
    clearErrorMessage,
} = actions

// verify user by unique id
export const verifyAPICall = (verifyID: string) => async (dispatch: any) => {
    dispatch(verifyNewHireLoading())
    try {
        const resp = await axios.get(`${API.NEWHIRE_URL}/newhires/verify/${verifyID}`, {
            headers: getRequestHeaders(),
        })
        const root = resp?.data
        const row =
            root && typeof root === "object" && "data" in root
                ? (root as { data: unknown }).data
                : undefined
        // Wrong gateway path / HTML 200 / proxy body → no nested `data`; avoid reading undefined['id'].
        if (!row || typeof row !== "object") {
            trackException(
                new Error("verify API: response missing expected JSON data object")
            )
            dispatch(
                verifyNewHiresError(
                    {
                        code: "FAILED_TO_PROCESS_REQUEST",
                        message: localizeErrorFromCode("FAILED_TO_PROCESS_REQUEST", ""),
                    }
                )
            )
            return
        }
        const d = row as Record<string, unknown>
        const id = d["id"]
        if (id == null || id === "") {
            trackException(new Error("verify API: response data missing id"))
            dispatch(
                verifyNewHiresError(
                    {
                        code: "FAILED_TO_PROCESS_REQUEST",
                        message: localizeErrorFromCode("FAILED_TO_PROCESS_REQUEST", ""),
                    }
                )
            )
            return
        }
        const apiResp: NewHireVerifyAPIResponse = {
            userID: String(id),
            onboardingTeamEmail: String(d["onboardingTeamEmail"] ?? ""),
            isInternal: Boolean(d["isInternal"]),
        }

        trackStackTrace(`user's magic link is verified`, { apiResp })
        dispatch(verifyNewHireSuccess({ verifyID, isVerified: true, ...apiResp }))
    } catch (err: any) {
        const apiError = parseAPIError(err)
        trackException(new Error(apiError.message))
        dispatch(
            verifyNewHiresError({
                code: apiError.code ?? FALLBACK_ERROR_CODE,
                message: localizeErrorFromCode(apiError.code, apiError.message),
            })
        )
    }
}

// validate user's asset tag
export const validateAssetLabelAPICall = (verifyId: string, assetTag: string, userID: string, isInternal: boolean) => async (dispatch: any) => {
    // API call to verify serial number
    dispatch(validateAssetLabelLoading())
    const body = JSON.stringify({ assetTag: assetTag.toUpperCase(), verifyID: verifyId, isInternal: isInternal })
    try {

        const resp = await axios.post(`${API.NEWHIRE_URL}/newhires/${userID}/verify/asset`, body, {
            headers: getRequestHeaders(),
        })
        trackStackTrace(`validate user's asset tabel finished`, { userID })
        dispatch(validateAssetLabelSuccess(resp.data))

    } catch (err: any) {
        const apiError = parseAPIError(err)
        trackException(new Error(apiError.message))
        dispatch(
            validateAssetLabelError({
                code: apiError.code ?? FALLBACK_ERROR_CODE,
                message: localizeErrorFromCode(apiError.code, apiError.message),
            })
        )
    }
}


export const validateHireDateApiCall = (verfyId: string, hireDate: string, userID: string, isInternal: boolean) => async (dispatch: any) => {
    dispatch(validateAssetLabelLoading())
    const body = JSON.stringify({ hireDate: hireDate, verifyID: verfyId, isInternal: isInternal })
    try {

        const resp = await axios.post(`${API.NEWHIRE_URL}/newhires/${userID}/verify/asset`, body, {
            headers: getRequestHeaders(),
        })
        trackStackTrace(`validate user's hire date finished`, { userID })
        dispatch(validateAssetLabelSuccess(resp.data))


    }
    catch (err: any) {
        const apiError = parseAPIError(err)
        trackException(new Error(apiError.message))
        dispatch(
            validateAssetLabelError({
                code: apiError.code ?? FALLBACK_ERROR_CODE,
                message: localizeErrorFromCode(apiError.code, apiError.message),
            })
        )
    }
}
// export reducer
export default reducer